/**
 * Buildings request serializer.
 *
 * Converts a normalized ListingQueryState<BuildingFilters> to the URLSearchParams
 * format expected by the existing datatableBuildingsPath() Kendo endpoint.
 *
 * Contract requirements (from the frozen contract §8 and parity matrix):
 *  - One URLSearchParams representation is used for BOTH listing fetches (page + sort +
 *    filters + search) AND CSV export params (filters + search only — no page/sort).
 *  - Multi-select arrays are order-normalized (sorted) before inclusion.
 *  - search is included when nonblank.
 *  - Text filters (name, externalId) use the 'contains' operator.
 *  - Boolean status uses 'eq' with string serialization ('true'/'false').
 *  - Multi-select filters use 'eq' operator.
 *
 * IP-15555: applyScopeToFilters injects state.scope.accountId as the account filter
 * for account-embed listings. Standalone must not send a parent account filter.
 * The parent id always replaces leftover account-filter values.
 * Scope is never persisted to state.filters.
 *
 * IP-15590: Non-sortable fields (siteId, inspectionsCount) produce a no-op if a
 * persisted sort lands on them — the serializer simply omits the sort params.
 */
import { normalizeMultiSelect } from '@hooks/listings';
import { NON_SORTABLE_COLUMN_KEYS } from '../types';
// ---------------------------------------------------------------------------
// Page / sort helpers
// ---------------------------------------------------------------------------
function appendPageParams(params, pageNumber, pageSize) {
    const skip = (pageNumber - 1) * pageSize;
    params.append('page', String(pageNumber));
    params.append('pageSize', String(pageSize));
    params.append('skip', String(skip));
    params.append('take', String(pageSize));
}
/**
 * IP-15590: omit sort params when the field is non-sortable.
 * The backend `sort_table` else-branch would still run on it, which can 500 the datatable.
 */
function appendSortParams(params, sort) {
    if (!sort)
        return;
    if (NON_SORTABLE_COLUMN_KEYS.has(sort.field))
        return;
    params.append('sort[0][field]', sort.field);
    params.append('sort[0][dir]', sort.direction);
}
// ---------------------------------------------------------------------------
// Filter param builder
// ---------------------------------------------------------------------------
/** Text fields that use the 'contains' operator (mirrors INITIAL_FILTERS.operators). */
const CONTAINS_FIELDS = new Set(['name', 'externalId']);
function buildFilterEntries(filters) {
    const entries = [];
    const op = (field) => (CONTAINS_FIELDS.has(field) ? 'contains' : 'eq');
    const addArray = (field, values) => {
        const normalized = normalizeMultiSelect([...values]);
        if (normalized.length === 0)
            return;
        entries.push({ field, operator: op(field), value: normalized });
    };
    const addText = (field, value) => {
        if (!value)
            return;
        entries.push({ field, operator: op(field), value });
    };
    const addBoolean = (field, value) => {
        if (value === null)
            return;
        entries.push({ field, operator: 'eq', value: String(value) });
    };
    // Multi-selects
    addArray('account', filters.account);
    addArray('tags', filters.tags);
    addArray('territory', filters.territory);
    addArray('additional_territory', filters.additionalTerritory);
    addArray('salesPerson', filters.salesPerson);
    addArray('compliancePlatform', filters.compliancePlatform);
    // Boolean select
    addBoolean('status', filters.status);
    // Text filters
    addText('name', filters.name);
    addText('externalId', filters.externalId);
    return entries;
}
function appendFilterParams(params, filters) {
    const entries = buildFilterEntries(filters);
    if (entries.length === 0)
        return;
    params.append('filter[logic]', 'and');
    entries.forEach((entry, i) => {
        const prefix = `filter[filters][${i}]`;
        params.append(`${prefix}[field]`, entry.field);
        params.append(`${prefix}[operator]`, entry.operator);
        if (Array.isArray(entry.value)) {
            entry.value.forEach((v) => {
                params.append(`${prefix}[value][]`, String(v));
            });
        }
        else {
            params.append(`${prefix}[value]`, String(entry.value));
        }
    });
}
// ---------------------------------------------------------------------------
// Scope injection (IP-15555)
// ---------------------------------------------------------------------------
/**
 * Injects the listing mount's parent account id into filter values for account-embed context.
 *
 * Scope lives on `state.scope` and is NOT written into persisted `state.filters`.
 * On account embed, inject the accountId as the account filter (replacing any leftover values).
 * Standalone mounts are unchanged — no account filter is added automatically.
 */
export function applyScopeToFilters(state) {
    if (state.scope.type === 'account') {
        return Object.assign(Object.assign({}, state.filters), { account: [state.scope.id] });
    }
    return state.filters;
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Full request serializer: page + sort + filters + search.
 * Used by the React Query fetch function and as the listing fetch params.
 */
export function serializeBuildingsRequest(state) {
    const params = new URLSearchParams();
    const filters = applyScopeToFilters(state);
    appendPageParams(params, state.page.number, state.page.size);
    appendSortParams(params, state.sort);
    appendFilterParams(params, filters);
    if (state.search)
        params.append('search', state.search);
    return params;
}
/**
 * Export/narrowing-only serializer: filters + search — NO page or sort.
 * Used for CSV export params so the server scopes exports to the same record set the user sees.
 */
export function serializeBuildingsNarrowing(state) {
    const params = new URLSearchParams();
    const filters = applyScopeToFilters(state);
    appendFilterParams(params, filters);
    if (state.search)
        params.append('search', state.search);
    return params;
}
